/**
 * Clinic Context Guard
 *
 * 🛡️ CRITICAL SAFETY MODULE
 * This module provides utilities to ensure clinic context is never lost
 * and provides recovery mechanisms if it does get lost.
 */

import { useAdditionalInfo } from "@/stores/domains/additionalInfo";

/**
 * Validates that the clinic context exists in storage
 * @returns true if both organization and location exist
 */
export const hasValidClinicContext = (): boolean => {
  const state = useAdditionalInfo.getState();
  return !!(state.organization?.value && state.location?.value);
};

/**
 * Gets the current clinic context
 * @returns Clinic context or null if not available
 */
export const getClinicContext = () => {
  const state = useAdditionalInfo.getState();

  if (!state.organization?.value || !state.location?.value) {
    return null;
  }

  return {
    organizationId: state.organization.value,
    organizationName: state.organization.label,
    clinicId: state.location.value,
    clinicName: state.location.label,
    roomId: state.room?.value || null,
    roomName: state.room?.label || null,
    roleId: state.role?.value || null,
    roleName: state.role?.label || null,
    terminalId: state.terminal?.value || null,
  };
};

/**
 * Validates clinic context on app initialization
 * Should be called in App.tsx or root component
 */
export const validateClinicContextOnInit = (): boolean => {
  const isValid = hasValidClinicContext();

  if (!isValid) {
    // This is normal during first login or after logout
    // User will be redirected to AdditionalInfo page to select organization/clinic
    console.info(
      "[ClinicContextGuard] Clinic context not set, user needs to select organization and clinic",
      {
        timestamp: new Date().toISOString(),
        currentPath: window.location.pathname,
      },
    );
  } else {
    const context = getClinicContext();
    console.info("[ClinicContextGuard] Clinic context validated", {
      organizationId: context?.organizationId,
      clinicId: context?.clinicId,
      timestamp: new Date().toISOString(),
    });
  }

  return isValid;
};

/**
 * Sets up a periodic check to ensure clinic context remains valid
 * This helps detect if localStorage gets corrupted or cleared
 */
export const setupClinicContextMonitor = () => {
  // Check every 30 seconds
  const interval = setInterval(() => {
    if (!hasValidClinicContext()) {
      console.error("[ClinicContextGuard] Clinic context lost during session!", {
        timestamp: new Date().toISOString(),
        currentPath: window.location.pathname,
      });

      // If context is lost and user is not on login or additional-info page, redirect
      const currentPath = window.location.pathname;
      const safeRoutes = ["/login", "/additional-info", "/404"];

      if (!safeRoutes.some((route) => currentPath.startsWith(route))) {
        console.warn("[ClinicContextGuard] Redirecting to additional-info page");
        window.location.href = "/additional-info";
      }
    }
  }, 30000); // 30 seconds

  // Return cleanup function
  return () => clearInterval(interval);
};

/**
 * Subscribes to clinic context changes and logs them
 * Useful for debugging context loss issues
 */
export const subscribeToClinicContext = (
  callback?: (context: {
    organizationId: string | null;
    organizationName: string | null;
    clinicId: string | null;
    clinicName: string | null;
    roomId: string | null;
    roomName: string | null;
    roleId: string | null;
    roleName: string | null;
    terminalId: string | null;
  }) => void,
) => {
  return useAdditionalInfo.subscribe((state) => {
    const context = {
      organizationId: state.organization?.value || null,
      organizationName: state.organization?.label || null,
      clinicId: state.location?.value || null,
      clinicName: state.location?.label || null,
      roomId: state.room?.value || null,
      roomName: state.room?.label || null,
      roleId: state.role?.value || null,
      roleName: state.role?.label || null,
      terminalId: state.terminal?.value || null,
    };

    console.debug("[ClinicContextGuard] Context updated", context);

    if (callback) {
      callback(context);
    }
  });
};
